<template>
	<div class="m-auto" v-if="!appIsLoaded && !isStart && !isSignUp">
		<div class="standby-box add-flex standby-box--container">
			<div class="standby-box--container">
				<img class="img-fluid mb-5" style="max-width: 35%;" src="@/assets/logo-white.png" alt="" />
				<div class="spinner mb-5">
					<div class="spinner-item"></div>
					<div class="spinner-item"></div>
					<div class="spinner-item"></div>
					<div class="spinner-item"></div>
					<div class="spinner-item"></div>
				</div>
				<div v-if="!isCapacitor" class="d-flex flex-column mx-4 mt-4">
					<div class="d-flex gap-2 align-items-baseline justify-content-center">
						<div class="fs-2 mb-3">Download the app on the App Store!</div>
						<div class="d-none badge badge-light-warning align-self-start">Beta</div>
					</div>
					<h2 class="text-gray-800 fs-4">Available on both the App Store<small>&trade;</small> and Google
						Play<small>&trade;</small></h2>
					<div class="mt-8 fw-bold text-gray-700 cursor-pointer" @click="signOut">
						Having trouble loading?
					</div>
				</div>
				<div v-else class="d-flex flex-column mx-4 mt-4">
					<div class="d-flex gap-2 align-items-baseline justify-content-center">
						<div class="fs-2 mb-3">A Refreshed Look is Coming Soon</div>
						<div class="d-none badge badge-light-warning align-self-start">Beta</div>
					</div>
					<h2 class="text-gray-800 fs-4">Easier scheduling, enhanced report designer and so much more!</h2>
					<div class="mt-8 fw-bold text-gray-700 cursor-pointer" @click="signOut">
						Having trouble loading?
					</div>
				</div>
			</div>
		</div>
	</div>
	<ApplicationWrapper v-if="!isLoading && isAuthenticated && !isStart && !isSignUp" />
	<router-view v-if="isSignUp" name="signup" />
	<router-view v-if="isStart" name="start" />
</template>

<script lang="ts">
import { defineComponent, onMounted } from "vue";
import { useApplication } from "@/store/pinia/applicationStore";
import ApplicationWrapper from "@/ApplicationWrapper.vue";
import auth0 from "@/auth";
import { useMockAuth } from "@/modules/authWrapper";
import { isPlatform } from "@ionic/vue";
import { useRoute } from "vue-router";
import { StatusBar, Style } from '@capacitor/status-bar';
import { env } from "@/env";

const isCypressTest = !!localStorage.getItem("auth0Cypress");
const auth = isCypressTest ? useMockAuth() : auth0;

export default defineComponent({
	components: { ApplicationWrapper },
	setup() {
		const applicationStore = useApplication();
		const route = useRoute();
		onMounted(async () => {
			window.initializeKT();
			if (isPlatform('capacitor')) {
				await StatusBar.setStyle({ style: Style.Dark });
				await StatusBar.setBackgroundColor({ color: '1E1E2D' })
			}
		});
		return { applicationStore, route };
	},
	methods: {
		signOut() {
			auth.logout({
				logoutParams: {
					returnTo: isPlatform("capacitor")
						? env.CAP_ENDSESSION
						: env.AUTH0_APPLOGOUT,
				},
			});
		},
	},
	computed: {
		currentRoute(): string {
			return this.route.fullPath;
		},
		isStart(): boolean {
			return this.route.name === "start";
		},
		isSignUp(): boolean {
			return this.route.name === "sign-up";
		},
		isLoading(): boolean {
			return auth.isLoading.value;
		},
		isAuthenticated(): boolean {
			return auth.isAuthenticated.value;
		},
		appIsLoaded(): boolean {
			return this.applicationStore.isLoaded;
		},
		isCapacitor(): boolean {
			return isPlatform("capacitor");
		},
	},
});
</script>

<style src="@vueform/multiselect/themes/default.css"></style>
<style lang="scss">
@import "element-plus/theme-chalk/index.css";

.spinner {
	--animation-duration: 1000ms;

	width: var(--size);
	height: var(--size);
	display: flex;
	align-items: center;
	justify-content: space-evenly;

	.spinner-item {
		width: calc(var(--size) / 12);
		height: 80%;
		background: var(--clr-spinner);
		animation: spinner1 var(--animation-duration) ease-in-out infinite;

		@keyframes spinner1 {
			50% {
				transform: scaleY(0.25);
			}
		}
	}

	.spinner-item:nth-child(1) {
		--clr-spinner: var(--clr1);
		animation-delay: calc(var(--animation-duration) / 10 * -3);
	}

	.spinner-item:nth-child(2) {
		--clr-spinner: var(--clr5);
		animation-delay: calc(var(--animation-duration) / 10 * -1);
	}

	.spinner-item:nth-child(3) {
		--clr-spinner: var(--clr3);
		animation-delay: calc(var(--animation-duration) / 10 * -2);
	}

	.spinner-item:nth-child(4) {
		--clr-spinner: var(--clr4);
		animation-delay: calc(var(--animation-duration) / 10 * -1);
	}

	.spinner-item:nth-child(5) {
		--clr-spinner: var(--clr2);
		animation-delay: calc(var(--animation-duration) / 10 * -3);
	}
}

:root {
	--size: 50px;
	--clr-bg: #272324;
	--clr1: #956aff;
	--clr2: #956aff;
	--clr3: #956aff;
	--clr4: #956aff;
	--clr5: #956aff;

	--ms-bg: #1E1E2D;
	--ms-border-color: #323248;
	--ms-border-width: 1px;
	--ms-radius: 0.475rem;

	--ms-dropdown-bg: #1E1E2D;
	--ms-dropdown-border-color: #323248;
	--ms-dropdown-border-width: 1px;
	--ms-dropdown-radius: 0.475rem;

	--ms-option-font-size: 1.1rem;
	--ms-option-line-height: 1.5;
	--ms-font-size: 1.1rem;
	--ms-line-height: 1.5;
	--ms-ring-color: #474761;
	--ms-ring-width: 1px;
	--ms-option-bg-selected: #956aff;
	--ms-option-bg-selected-pointed: #1E1E2D;
	--ms-option-bg-pointed: #2F264F;
	--ms-option-color-pointed: #FFFFFF;
	--ms-py: 0.75rem;
	--ms-px: 1rem;

	--ms-tag-font-size: 1.1rem;
	--ms-tag-line-height: 1.5;
	--ms-tag-bg: #956aff;
	--ms-tag-color: #FFFFFF;
	--el-color-primary: #956aff !important;
	--el-input-border-color: #2B2B40 !important;
	--el-datepicker-header-text-color: #8F93A4 !important;
	--fc-button-active-bg-color: #956aff !important;
	--fc-button-text-color: #fff !important;
	--el-border-color-base: #2B2B40 !important;
	//--el-input-border-color: #323248 !important;
	--el-bg-color: #1E1E2D !important;
	--el-bg-color-overlay: #1E1E2D !important;
	--el-fill-color-blank: #1E1E2D !important;
	--el-input-bg-color: unset;
	--el-text-color-regular: white !important;
	--el-text-color-primary: white !important;
	--el-fill-color-light: #956aff !important;
	--fk-max-width-input: 100%;
	--el-border-color-light: #323248 !important;
	--el-datepicker-inrange-bg-color: #956aff !important;
	--el-button-bg-color: #956aff !important;
	--el-dropdown-menu-box-shadow: green !important;
	--el-dropdown-menuItem-hover-fill: red !important;
	--el-dropdown-menuItem-hover-color: red !important;

	--pka-scale: 1rem;
	--pka-color-accent: #956AFF !important;
	--pka-color-black: 29, 41, 57;
	--pka-color-darker: #121212 !important;
	--pka-color-dark: #121212 !important;
	--pka-color-light: white !important;
	--pka-color-lighter: 243, 244, 246;
	--pka-color-white: 255, 255, 255;
	--pka-border-radius: 6px;
	--pka-font-family: system-ui, "Inter", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol" !important;
	--pka-z-index: 9999;
}

.pka-panel-suggestion.pka-active {
	background-color: #956aff !important;
}

body.dark .pka-panel,
body[data-theme="dark"] .pka-panel {
	background-color: #1E1E2D !important;
	border-color: #323248 !important;
}

.el-dropdown-menu__item:not(.is-disabled):focus {
	background-color: var(--el-color-primary);
	color: white;
}

.el-button:not(.el-button--primary) {
	background-color: #323248;
	border-color: #323248;
}

.el-button:not(.el-button--primary):hover {
	background-color: #474761;
	border-color: #474761;
}

.el-popper.is-dark .el-popper__arrow::before {
	border: 1px solid #2B2B40 !important;
	border-top-color: rgb(43, 43, 64);
	border-left-color: rgb(43, 43, 64);
	background: transparent !important;
	right: 0;
}

.el-date-table td.in-range .el-date-table-cell {
	background-color: #956aff !important;
}

.el-date-table td.available:hover {
	color: white !important;
}

.el-date-table td.selected .el-date-table-cell {
	background-color: unset !important;
}

.el-date-table td.today.selected .el-date-table-cell__text {
	color: white !important;
}

.el-button.is-plain {
	color: white !important;
	background-color: #956aff !important;
}

.fc-button-active {
	background-color: #956aff !important;
	color: #fff !important;
}

.el-picker__popper.el-popper[role="tooltip"] {
	border: 1px solid #2B2B40 !important;
}

.el-popper.is-dark {
	background-color: #1E1E2D !important;
	border: 1px solid #2B2B40 !important;
	color: #fff !important;
}

.el-popover.el-popper,
.el-popper.is-light .el-popper__arrow::before {
	background-color: #1E1E2D !important;
	border: 1px solid #2B2B40 !important;
}

.multiselect-tags-search {
	background: #1E1E2D;
	color: white;
}

.standby-box {
	text-align: center;
}

.add-flex {
	flex: 1;
	display: flex;
}

.standby-box--container {
	display: flex;
	flex-wrap: wrap;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	align-content: center;
}

.el-loading-mask {
	z-index: 1000 !important;
}

.el-input {
	width: 100% !important;
	--el-input-background-color: #1b1b29 !important;
	--el-input-font-color: #FFFFFF !important;
	--el-input-border-radius: 0.475rem !important;
	--el-input-icon-color: #8F93A4 !important;
	--el-input-placeholder-color: #FFFFFF !important;
}

.el-input__inner {
	// border: #323248 solid 1px !important;
	// background-color: #1b1b29 !important;
	color: white;
}

.el-input--prefix .el-input__inner {
	padding-left: 10px !important;
}

.el-input__inner:focus {
	border: #956aff solid 1px !important;
}

.el-picker__popper,
.el-picker-panel,
.el-picker-panel__footer {
	background-color: #1b1b29 !important;
	color: white;
}

.el-select__popper {
	background-color: #1b1b29 !important;
	border: 1px solid #323248 !important;
	color: white;
}

.el-date-picker__header {
	color: white !important;
}

.el-button--default {
	background-color: #956aff !important;
	border: 0 !important;
	color: white;
}

.el-button--default:hover {
	background-color: #7337EE !important;
	border: 0 !important;
	color: white !important;
}

.el-select-dropdown__item.hover,
.el-select-dropdown__item:hover {
	background-color: #956aff !important;
	color: white;
}

.el-select-dropdown__item {
	color: white !important;
}

.el-date-table td.current:not(.disabled) span {
	background-color: #956aff !important;
	color: white;
}

.el-date-table td.today:not(.current) span {
	color: #956aff !important;
}

.el-date-table,
.el-date-picker__header-label,
.el-picker-panel__icon-btn {
	--el-datepicker-header-font-color: white !important;
	--el-text-color-regular: white !important;
	--el-datepicker-icon-color: white !important;
}

.el-loading-mask {
	background-color: #1E1E2D !important;
}

.el-loading-spinner .path {
	stroke: #956aff !important;
}

.el-switch__label {
	--el-text-color-primary: white;
}

.el-switch__label.is-active {
	color: white !important;
}

.el-switch {
	--el-switch-on-color: #956aff !important;
}

.el-input__inner,
.el-date-editor--date,
.el-date-editor--dates,
.el-date-editor--timerange {
	min-height: calc(1.5em + 1.65rem + 2px);
}

.el-input__wrapper {
	background-color: unset !important;
	box-shadow: 0 0 0 1px #323248 inset !important;
}

.el-input.is-invalid>.el-input__inner,
.el-select.is-invalid .el-input__inner {
	border-color: #F64E60 !important;
	padding-right: calc(1.5em + 1.5rem);
	background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23F64E60'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23F64E60' stroke='none'/%3e%3c/svg%3e");
	background-repeat: no-repeat;
	background-position: right calc(0.375em + 0.375rem) center;
	background-size: calc(0.75em + 0.75rem) calc(0.75em + 0.75rem);
}

.fc-event-main {
	cursor: pointer;
}

.ql-snow.ql-toolbar button.ql-active,
.ql-snow .ql-toolbar button.ql-active,
.ql-snow.ql-toolbar .ql-picker-label.ql-active,
.ql-snow .ql-toolbar .ql-picker-label.ql-active,
.ql-snow.ql-toolbar .ql-picker-item.ql-selected,
.ql-snow .ql-toolbar .ql-picker-item.ql-selected {
	background-color: #323248 !important;
	color: #B7B7C8 !important;
}

.ql-snow .ql-tooltip {
	color: #FFFFFF !important;
	background-color: #1E1E2D !important;
	background-clip: padding-box;
	box-shadow: unset !important;
	border: 1px solid #323248 !important;
	padding: 5px 12px;
	white-space: nowrap;
}

.ql-snow .ql-remove {
	color: #F64E60 !important;
}

.ql-snow .ql-action {
	color: #956aff !important;
}

.ql-tooltip.ql-editing .ql-action {
	color: white !important;
	background-color: #956aff !important;
	border-radius: 0.475rem;
	padding: calc(0.55rem + 1px) calc(1.25rem + 1px);
	margin-left: 10px !important;
}

.ql-tooltip.ql-editing .ql-action::after {
	margin-left: unset !important;
}

.ql-snow .ql-picker.ql-expanded .ql-picker-options .ql-picker-item {
	color: white !important;
}

.ql-snow a {
	color: #956aff !important;
}

.ql-snow.ql-toolbar button.ql-active .ql-stroke,
.ql-snow .ql-toolbar button.ql-active .ql-stroke,
.ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke,
.ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke,
.ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke,
.ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke,
.ql-snow.ql-toolbar button.ql-active .ql-stroke-miter,
.ql-snow .ql-toolbar button.ql-active .ql-stroke-miter,
.ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke-miter,
.ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke-miter,
.ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke-miter,
.ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke-miter {
	stroke: #956aff !important;
}

.ql-snow.ql-toolbar button.ql-active,
.ql-snow .ql-toolbar button.ql-active,
.ql-snow.ql-toolbar .ql-picker-label.ql-active,
.ql-snow .ql-toolbar .ql-picker-label.ql-active,
.ql-snow.ql-toolbar .ql-picker-item.ql-selected,
.ql-snow .ql-toolbar .ql-picker-item.ql-selected {
	color: #956aff !important;
}

.ql-snow.ql-toolbar button.ql-active .ql-fill,
.ql-snow .ql-toolbar button.ql-active .ql-fill,
.ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-fill,
.ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-fill,
.ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-fill,
.ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-fill,
.ql-snow.ql-toolbar button.ql-active .ql-stroke.ql-fill,
.ql-snow .ql-toolbar button.ql-active .ql-stroke.ql-fill,
.ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke.ql-fill,
.ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke.ql-fill,
.ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke.ql-fill,
.ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke.ql-fill {
	fill: #956aff !important;
}

.ql-snow.ql-toolbar button:hover,
.ql-snow .ql-toolbar button:hover,
.ql-snow.ql-toolbar button:focus,
.ql-snow .ql-toolbar button:focus,
.ql-snow.ql-toolbar .ql-picker-label:hover,
.ql-snow .ql-toolbar .ql-picker-label:hover,
.ql-snow.ql-toolbar .ql-picker-item:hover,
.ql-snow .ql-toolbar .ql-picker-item:hover {
	background-color: #323248 !important;
}

.ql-toolbar.ql-snow,
.ql-container.ql-snow {
	border: 1px solid #2B2B40 !important;
}

.fc-license-message {
	background-color: #323248 !important;
	display: none;
}

.fc-timeline-slot-frame a {
	color: #B7B7C8 !important;
}

.table> :not(:first-child) {
	border-top: unset !important;
}
</style>
